import { isPlatformBrowser } from '@angular/common';
import { effect, inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AlertComponent } from '@banyan/commons/components/alert/alert.component';
import { EXPIRED_PROPOSAL_TIME, NEW_PROPOSAL_TIME } from '@banyan/commons/consts/global.const';
import { AccountService } from '@banyan/features/account/services/account.service';
import { QuoteService } from '@banyan/features/request/services/quote.service';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';

import { FeatureTogglingService } from './feature-toggling.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly _snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly platformId = inject(PLATFORM_ID);
  ngZone = inject(NgZone);

  accountService = inject(AccountService);
  quoteService = inject(QuoteService);
  featureTogglingService = inject(FeatureTogglingService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        if (this.accountService.userLoaded()) {
            this.quoteService.getExpiringProposalsV2();
            this.quoteService.loadNewProposalV2();
            this.ngZone.runOutsideAngular(() => {
              timer(EXPIRED_PROPOSAL_TIME).subscribe(() => {
                if ((this.quoteService.expiringProposalsCount() ?? 0) > 0) {
                  this.callAlertV2(this.quoteService.expiringProposalsCount() ?? 0);
                }
              });
              timer(NEW_PROPOSAL_TIME).subscribe(() => {
                if ((this.quoteService.newProposalsV2()?.count ?? 0) > 0) {
                  this.callAlertNewProposalV2(this.quoteService.newProposalsV2()?.requests[0].id, this.quoteService.newProposalsV2()?.requests[0].endUses[0].id, this.quoteService.newProposals()?.count ?? 0);
                }
              });
            });
        }
      });
    }
  }

  callAlert(expiringProposalsCount: number): void {
    const params = {
      text: this.translate.instant(
        'notification.expiring-proposals.description',
        { count: expiringProposalsCount },
      ),
      icon: 'info',
      button: this.translate.instant('notification.expiring-proposals.button'),
      closeButton: true,
      url: '/protected/proposal',
    };
    this._snackBar.openFromComponent(AlertComponent, {
      data: params,
      duration: 5000,
    });
  }


  callAlertNewProposal(
    quoteId: number | undefined,
    expiringProposalsCount: number,
  ): void {
    const params = {
      text: this.translate.instant('notification.new-proposal.description', {
        count: expiringProposalsCount,
      }),
      icon: 'info',
      button: this.translate.instant('notification.new-proposal.button'),
      closeButton: true,
      url: `/protected/proposal/${quoteId}`,
    };
    this._snackBar.openFromComponent(AlertComponent, {
      data: params,
      duration: 5000,
    });
  }

  callAlertV2(expiringProposalsCount: number): void {
    const params = {
      text: this.translate.instant(
        'notification.expiring-proposals.description',
        { count: expiringProposalsCount },
      ),
      icon: 'info',
      button: this.translate.instant('notification.expiring-proposals.button'),
      closeButton: true,
      url: '/protected/proposal/requests',
    };
    this._snackBar.openFromComponent(AlertComponent, {
      data: params,
      duration: 5000,
    });
  }


  callAlertNewProposalV2(
    requestId: number | undefined,
    endUseId: number | undefined,
    expiringProposalsCount: number,
  ): void {
    const params = {
      text: this.translate.instant('notification.new-proposal.description', {
        count: expiringProposalsCount,
      }),
      icon: 'info',
      button: this.translate.instant('notification.new-proposal.button'),
      closeButton: true,
      url: `/protected/proposal/requests`,
    };
    this._snackBar.openFromComponent(AlertComponent, {
      data: params,
      duration: 5000,
    });
  }
}
